import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { Menu, MenuItem, Divider, ListItemIcon, Modal, TextField } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import logoHighRes from '../res/logoHighRes.PNG';
import { signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { SpaceDashboard } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import { pinkColor } from '../utils/constants';

const darkFieldSx = {
    '& .MuiOutlinedInput-root': {
        color: 'white',
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: '#555' },
        '&.Mui-focused fieldset': { borderColor: pinkColor },
    },
    '& .MuiInputLabel-root': { color: '#888' },
    '& .MuiInputLabel-root.Mui-focused': { color: pinkColor },
    '& .MuiInputAdornment-root .MuiIconButton-root': { color: '#888' },
};

export default function BasicAppBar({ title, bgColor }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUser(user);
        });
        return () => unsubscribe();
    }, []);

    const handleSignIn = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            const tokenResult = await user.getIdTokenResult();
            const role = tokenResult.claims.role;

            setModalOpen(false);
            setEmail('');
            setPassword('');
            setError(null);

            if (role === 'admin' || role === 'manager' || role === 'staff') {
                navigate('/dashboard');
            } else {
                navigate('/');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Sign in failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        if (loading) return; // prevent close while signing in
        setModalOpen(false);
        setEmail('');
        setPassword('');
        setError(null);
        setShowPassword(false);
    };

    const handleAvatarMenuClose = () => setAnchorEl(null);
    const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);

    const handleLogOut = () => {
        signOut(auth);
        setAnchorEl(null);
        navigate('/');
    };

    const handleDashboardClick = () => {
        navigate('/dashboard');
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        navigate('/profile');
        setAnchorEl(null);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar
                position="sticky"
                color={bgColor}
                elevation={0}
                sx={{
                    backgroundColor: '#171514',
                    borderBottom: '1px solid rgba(183, 154, 98, 0.35)',
                }}
            >
                <Toolbar>
                    <Box
                        component="img"
                        src={logoHighRes}
                        alt="Logo"
                        width="38"
                        height="40"
                        onClick={() => navigate('/')}
                        sx={{ height: 40, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <Typography
                        variant="h6"
                        component="div"
                        noWrap
                        sx={{
                            flexGrow: 1,
                            marginLeft: 1.5,
                            fontFamily: "'Newsreader', 'Iowan Old Style', Georgia, serif",
                            fontWeight: 400,
                            letterSpacing: '-0.01em',
                            color: '#f3efe8',
                            fontSize: { xs: '1.15rem', sm: '1.5rem' },
                        }}
                    >
                        {title}
                    </Typography>
                    {user ? (
                        <>
                            <IconButton
                                onClick={handleAvatarClick}
                                size="small"
                                sx={{ ml: 2 }}
                                aria-controls={anchorEl ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={anchorEl ? 'true' : undefined}
                            >
                                <Avatar
                                    alt={user.displayName}
                                    sx={{
                                        bgcolor: '#1a1a1a',
                                        color: pinkColor,
                                        border: `2px solid ${pinkColor}`,
                                        fontWeight: 700,
                                    }}
                                >
                                    {user.displayName ? user.displayName.split(' ')[0].charAt(0) : ''}
                                </Avatar>
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                id="account-menu"
                                open={Boolean(anchorEl)}
                                onClose={handleAvatarMenuClose}
                                onClick={handleAvatarMenuClose}
                            >
                                <MenuItem onClick={handleDashboardClick}>
                                    <ListItemIcon>
                                        <SpaceDashboard fontSize="small" />
                                    </ListItemIcon>
                                    Dashboard
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={handleProfileClick}>
                                    <ListItemIcon>
                                        <SettingsIcon fontSize="small" />
                                    </ListItemIcon>
                                    Profile
                                </MenuItem>
                                <MenuItem onClick={handleLogOut}>
                                    <ListItemIcon>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemIcon>
                                    Logout
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <Button
                            color="inherit"
                            onClick={() => setModalOpen(true)}
                            sx={{
                                fontFamily: "'Figtree', system-ui, sans-serif",
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                color: '#f3efe8',
                                border: '1.5px solid rgba(243, 239, 232, 0.4)',
                                borderRadius: '999px',
                                px: 2.5,
                                minHeight: 40,
                                '&:hover': { borderColor: '#f3efe8', backgroundColor: 'transparent' },
                            }}
                        >
                            Login
                        </Button>
                    )}
                </Toolbar>
            </AppBar>

            <Modal
                open={modalOpen}
                onClose={handleModalClose}
                aria-labelledby="login-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    overflowY: 'auto',
                    pt: { xs: 6, sm: 10 },
                    pb: 4,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#111',
                        border: '1px solid #222',
                        borderRadius: 2,
                        padding: '1.5rem',
                        width: '90vw',
                        maxWidth: 400,
                        boxSizing: 'border-box',
                        outline: 'none',
                    }}
                >
                    <Box component="img" src={logoHighRes} alt="Logo" sx={{ width: 100, mb: 2 }} />
                    <Typography id="login-modal-title" component="h1" variant="h5" sx={{ color: 'white', mb: 0.5 }}>
                        Welcome back!
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Sign in to continue
                    </Typography>

                    <Box component="form" noValidate onSubmit={handleSignIn} sx={{ width: '100%', mt: 1 }}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            sx={darkFieldSx}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            sx={darkFieldSx}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((v) => !v)}
                                            edge="end"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {error && (
                            <Typography sx={{ color: '#f44336', fontSize: '0.85rem', mt: 1, textAlign: 'center' }}>
                                {error}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 2, mb: 1,
                                backgroundColor: pinkColor,
                                '&:hover': { backgroundColor: '#c4287a' },
                                '&.Mui-disabled': { backgroundColor: '#3a1a2a', color: '#666' },
                                height: 44,
                            }}
                        >
                            {loading
                                ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                : 'Sign In'
                            }
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
